import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import CompanyDetailsOutputComponent from '../output/CompanyDetailsOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const CIN_REGEX = /^[LU]\d{5}[A-Z]{2}\d{4}[A-Z]{3}\d{6}$/;
const CIN_LENGTH = 21;

const COMPANY_TYPE_CODES = {
    PTC: 'Private Limited',
    PLC: 'Public Limited',
    OPC: 'One Person Company',
    NPL: 'Not for Profit (Sec 8)',
    GOI: 'Government of India',
    SGC: 'State Government Company',
    GAP: 'Guarantee & Association',
    GAT: 'Guarantee with Share Capital',
    ULL: 'Unlimited Liability',
    ULT: 'Unlimited with Share Capital',
    FLC: 'Financial Lease Company',
};

const CompanyDetailsInputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({ id_number: '', consent: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    const isValidCin = CIN_REGEX.test(formData.id_number);

    const companyTypeHint = (() => {
        const cin = formData.id_number;
        if (cin.length < 14) return null;
        return COMPANY_TYPE_CODES[cin.substring(11, 14)] || null;
    })();

    const handleCinChange = (e) => {
        const value = (e.target.value || '').toUpperCase().trim();
        setFormData((prev) => ({ ...prev, id_number: value }));
        if (errors.id_number) setErrors((prev) => ({ ...prev, id_number: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        if (!formData.id_number) next.id_number = 'CIN number is required';
        else if (!CIN_REGEX.test(formData.id_number)) next.id_number = 'Invalid CIN format (e.g., U74999MH2020PTC123456)';
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submitFetch = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/company-details', {
                id_number: formData.id_number,
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Lookup failed');
            setReportData(res.data);
            toast.current?.show({
                severity: 'success', summary: 'Company details fetched', detail: 'Report ready below', life: 2000,
            });
            // Credits just changed. Invalidating updates every mounted consumer at
            // once and is ordered strictly after the deduction.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error('Company details fetch failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const map = {
                500: 'Internal server error. Please try again later.',
                422: 'Please check your CIN number and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid CIN number. Please verify and retry.',
                404: 'No company found for this CIN.',
            };
            let detail = map[status] || error.response?.data?.message || error.message || 'Lookup failed';
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            toast.current?.show({ severity: 'error', summary: 'Lookup failed', detail, life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">CIN:</span> <span className="text-900 font-medium">{formData.id_number}</span></div>
                    {companyTypeHint && (
                        <div className="mb-2"><span className="text-600">Type:</span> <span className="text-900 font-medium">{companyTypeHint}</span></div>
                    )}
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm Company Details lookup',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, fetch',
            rejectLabel: 'Cancel',
            accept: submitFetch,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const handleFetchAnother = () => {
        setReportData(null);
        setFormData({ id_number: '', consent: false });
        setErrors({});
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            <ReportBanner
                icon="pi-building"
                title="Company Details"
                subtitle="Fetch company details from the MCA database using a CIN number"
                availableCredits={serviceDetails?.available_credits}
                requiredCredits={serviceDetails?.credits_required}
                loading={fetchingService}
            />

            <Card>
                {!reportData ? (
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="grid">
                            <div className="field col-12 md:col-8 lg:col-6">
                                <label htmlFor="cin" className="block font-medium text-sm mb-2">
                                    CIN Number <span className="text-red-500">*</span>
                                </label>
                                <IconField iconPosition="left" className="w-full">
                                    <InputIcon className="pi pi-building" />
                                    <InputText
                                        id="cin"
                                        value={formData.id_number}
                                        onChange={handleCinChange}
                                        placeholder="e.g. U74999MH2020PTC123456"
                                        maxLength={CIN_LENGTH}
                                        autoCapitalize="characters"
                                        spellCheck={false}
                                        disabled={loading}
                                        className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.id_number })}
                                        aria-describedby="cin-help"
                                        aria-invalid={!!errors.id_number}
                                    />
                                </IconField>
                                {errors.id_number && (
                                    <small id="cin-help" className="p-error block mt-1" role="alert">
                                        <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                        {errors.id_number}
                                    </small>
                                )}
                                {!errors.id_number && isValidCin && (
                                    <small className="text-green-500 block mt-1">
                                        <i className="pi pi-check-circle mr-1" aria-hidden="true" />
                                        Valid CIN format{companyTypeHint && <> · {companyTypeHint}</>}
                                    </small>
                                )}
                            </div>
                        </div>

                        <ConsentCheckbox
                            checked={formData.consent}
                            onChange={handleConsentChange}
                            error={errors.consent}
                            reportType="company-details"
                        />

                        <div className="flex justify-content-end mt-3">
                            <Button
                                type="submit"
                                label="Fetch Company Details"
                                icon="pi pi-arrow-right"
                                iconPos="right"
                                loading={loading}
                                disabled={loading}
                                size="small"
                            />
                        </div>
                    </form>
                ) : (
                    <>
                        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-check-circle text-green-500 pi-2x" aria-hidden="true" />
                                <div>
                                    <p className="text-sm font-semibold text-900 m-0">Company details fetched</p>
                                    <p className="text-xs text-500 m-0">Showing report for {formData.id_number}</p>
                                </div>
                            </div>
                            <Button
                                label="Fetch another"
                                icon="pi pi-refresh"
                                severity="secondary"
                                outlined
                                size="small"
                                onClick={handleFetchAnother}
                            />
                        </div>
                        <CompanyDetailsOutputComponent reportData={reportData} />
                    </>
                )}
            </Card>
        </>
    );
};

export default CompanyDetailsInputComponent;
